import { StoryTweetCard } from '../uikit/story-tweet-card/StoryTweetCard';
import React, { FunctionComponent, useEffect } from 'react';
import { useAccount } from '../../hooks/useAccount';
import { Tooltip } from '../ui';
import { AppSetting, BooleanSettingValue } from '../../services/models/app-settings';
import { Story } from '../../services/models/story';
import { StoryTweet } from '../../services/models/story-tweet';
import StoryCardLink from '../story-card-link/StoryCardLink';
import StoryCardShareOptions from '../story-card-share-options/StoryCardShareOptions';

const useOpenInNewTab = () => {
  const { account } = useAccount();
  const settings = account?.settings || [];
  const newTabSetting = settings.find(x => x.name === AppSetting.NewTab);
  return !!newTabSetting ? newTabSetting.value === BooleanSettingValue.Yes : true;
};

interface BriefStoryCardProps {
  briefId: number;
  story: Story;
  isPreview?: boolean;
  requestStoryTweets?: (story: Story) => void;
  titleClick?: (storyId: number, storyUri: string) => void;
}

const BriefStoryCard: FunctionComponent<BriefStoryCardProps> = ({
  briefId,
  story,
  titleClick,
  isPreview,
  requestStoryTweets,
}) => {
  const openInNewTab = useOpenInNewTab();

  const goToTwitter = (tweet: StoryTweet) => {
    const url = `http://twitter.com/${tweet.username}`;

    if (openInNewTab) {
      window.open(url, '_blank');
    } else {
      window.location.href = url;
    }
  };

  useEffect(() => {
    if (requestStoryTweets && !story.isFetchingTweets && !story.tweetsLoaded) {
      requestStoryTweets(story);
    }
  }, [requestStoryTweets, story]);

  const getStoryTools = () => {
    if (isPreview) return null;

    return (
      <div className="col-span-3 flex items-center justify-between pt-2.5">
        {/* Share buttons: hidden until card hover on fine-pointer (mouse) devices,
            always visible on touch. The `group/card` lives on the root below;
            focus-within keeps them reachable when tabbing through the card. */}
        <StoryCardShareOptions
          briefId={briefId}
          story={story}
          isPreview={isPreview}
          className="pointer-fine:invisible pointer-fine:group-hover/card:visible pointer-fine:group-focus-within/card:visible"
        />

        <div className="flex flex-col items-center">
          <Tooltip content={<>Interest Score - {story.scoreLabel}</>}>
            <div>
              <div className="text-center text-[1.15em]">
                <span className="text-primary-deep">{story.score}</span>
                <span className="text-gray-400">/100</span>
              </div>
              <div className="h-2 w-[60px] overflow-hidden rounded-full bg-gray-100 shadow-inner">
                <div
                  className="h-full rounded-full transition-[width] duration-[600ms] ease-out"
                  style={{ width: `${story.score}%`, backgroundColor: `#${story.scoreHexColor}` }}
                />
              </div>
            </div>
          </Tooltip>
        </div>
      </div>
    );
  };

  const getStoryDate = () => {
    return new Intl.DateTimeFormat('en', {
      month: 'short',
      day: 'numeric',
    }).format(new Date(story.publicationDate));
  };

  const renderTweets = () => {
    if (story.tweets && story.tweets.length > 0) {
      const currentTweet = story.tweets[0];
      return (
        <StoryTweetCard
          tweet={currentTweet}
          openClickCommand={() => {
            goToTwitter(currentTweet);
          }}
        />
      );
    }
  };

  // The max-lg:not-first divider reproduces the legacy mobile `::before` rule
  // (Bootstrap md ≈ 991.98px → Tailwind max-lg 1024px, ~32px wider). It sits on
  // the padded root so the line lands at the card's top edge, as the legacy
  // `top:-16px` offset did.
  return (
    <div className="group/card cursor-default p-4 max-lg:not-first:border-t max-lg:not-first:border-gray-200">
      <div>
        <article className="grid grid-cols-[1fr_16px_auto] grid-rows-[repeat(6,minmax(0px,auto))]">
          {story.imageUrl && (
            <figure className="col-start-3 row-span-3 row-start-1 m-0 h-[100px] w-[100px]">
              <StoryCardLink
                isClicked={story.isClicked}
                briefId={briefId}
                storyId={story.id}
                href={story.url}
              >
                <img
                  className="block h-full w-full rounded-sm bg-gray-100 object-cover"
                  src={story.imageUrl}
                  alt={story.title}
                  loading="lazy"
                />
              </StoryCardLink>
            </figure>
          )}

          <div className="col-start-1 flex flex-wrap text-[0.7rem] font-bold uppercase text-gray-400">
            {story.source}
          </div>

          <h2 className="col-start-1 m-0 flex text-[1.25rem] font-bold leading-[1.25]">
            {/* titleClick (flag-gated) opens the in-app reader; without it the
                link goes straight to the article. */}
            <StoryCardLink
              isClicked={story.isClicked}
              briefId={briefId}
              storyId={story.id}
              href={story.url}
              titleClick={titleClick}
            >
              {story.title}
            </StoryCardLink>
          </h2>

          <div className="col-start-1 mt-[5px] flex text-[0.8125rem] text-gray-500">
            {story.author && <span>{story.author}&nbsp;–&nbsp;</span>}
            <time dateTime={story.publicationDate.toLocaleString()}>{getStoryDate()}</time>
          </div>

          <div className="col-span-3 mt-2.5 flex break-words text-[0.8125rem] leading-[1.3] opacity-80">
            {story.excerpt}
          </div>

          {getStoryTools()}
        </article>

        {renderTweets()}
      </div>
    </div>
  );
};

export default BriefStoryCard;
